import Button from "@components/Button"
import Layout from "@components/Layout"
import PageHeader from "@components/PageHeader"
import PasswordInput from "@components/PasswordInput"
import ResetPasswordDialog from "@components/ResetPasswordDialog"
import Spinner from "@components/Spinner"
import {
  authEmailLinkFor,
  verifyAuthEmailLinkOnce,
} from "@helpers/authEmailLink"
import { supabase, updatePassword } from "@helpers/supabase"
import { validatePassword } from "@helpers/validations"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useLocation, useNavigate } from "react-router-dom"

type ResetPasswordState = "verifying" | "form" | "linkExpired"

export default function ResetPasswordPage() {
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false)
  const [password, setPassword] = useState<string>("")

  const { pathname } = useLocation()
  // NOTE: The reset email's link: a `token_hash` to verify here, or (links
  // sent before the email templates changed) the recovery session in the
  // fragment, which supabase-js picks up, or `error_code` (e.g. `otp_expired`)
  // when Supabase already found the link expired or used.
  const [link] = useState(() => authEmailLinkFor(pathname))
  const [state, setState] = useState<ResetPasswordState>(() => {
    if (link.kind === "error") return "linkExpired"
    if (link.kind === "tokenHash") return "verifying"
    return "form"
  })

  const navigate = useNavigate()
  const { t } = useTranslation()

  useEffect(() => {
    if (link.kind !== "tokenHash") return

    let isCurrent = true
    // NOTE: Right away, not on submit: the token works once, and verifying it
    // makes it useless to anything else that saw the link.
    verifyAuthEmailLinkOnce(
      (params) => supabase.auth.verifyOtp(params),
      link.tokenHash,
      link.type ?? "recovery",
    )
      .then(({ error }) => {
        if (!isCurrent) return
        if (error) console.warn("Verifying the reset link failed", error.code)
        setState(error ? "linkExpired" : "form")
      })
      .catch((error: unknown) => {
        console.error(error)
        if (isCurrent) setState("linkExpired")
      })

    return () => {
      isCurrent = false
    }
  }, [link])

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    const { error } = await updatePassword({ password })

    if (error) {
      if (
        error.message ===
        "New password should be different from the old password."
      ) {
        setErrorMessage(
          t("pages.account.resetPassword.newPasswordShouldBeDifferent"),
        )
      } else {
        throw error
      }
    } else {
      setIsDialogOpen(true)
    }
  }

  if (state === "verifying") {
    return (
      <Layout>
        <div
          aria-busy="true"
          className="flex grow flex-col items-center justify-center"
        >
          <Spinner />
        </div>
      </Layout>
    )
  }

  if (state === "linkExpired") {
    return (
      <Layout>
        <PageHeader
          emoji="⏰"
          heading={t("pages.account.resetPassword.linkExpired.heading")}
          subheading={t("pages.account.resetPassword.linkExpired.text")}
        />

        <div className="custom-form">
          <div className="flex grow flex-col justify-end">
            <Button
              onClick={() => navigate("/account/forgot-password")}
              title={t(
                "pages.account.resetPassword.linkExpired.requestNewLink",
              )}
            />
          </div>
        </div>
      </Layout>
    )
  }

  return (
    <>
      <Layout>
        <PageHeader
          emoji="🔑"
          heading={t("pages.account.resetPassword.heading")}
          subheading={
            errorMessage ?? t("pages.account.resetPassword.enterNewPassword")
          }
          hasError={!!errorMessage}
        />

        <form className="custom-form" onSubmit={handleSubmit}>
          <div className="flex grow flex-col justify-between">
            <PasswordInput
              autoFocus
              onChange={(event) => setPassword(event.target.value)}
            />

            <Button
              disabled={!validatePassword(password)}
              title={t("pages.account.forgotPassword.resetPassword")}
              type="submit"
            />
          </div>
        </form>
      </Layout>

      {isDialogOpen && (
        <ResetPasswordDialog
          open={isDialogOpen}
          onClose={() => setIsDialogOpen(false)}
        />
      )}
    </>
  )
}
